/**
 * CardSectionListItem — A single sortable row in the card section list.
 *
 * Uses @dnd-kit/sortable (same pattern as SectionListItem.tsx).
 * Shows: drag handle, icon, label, expand chevron (if has settings), remove button.
 */

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, ChevronDown, X } from 'lucide-react'
import { CARD_SECTION_META } from './card-editor-types'
import { CardSectionSettings } from './CardSectionSettings'
import type { CardSection } from './card-editor-types'

interface CardSectionListItemProps {
  section: CardSection
  isExpanded: boolean
  onToggleExpand: () => void
  onRemove: () => void
  onConfigChange: (key: string, value: string) => void
}

export function CardSectionListItem({
  section,
  isExpanded,
  onToggleExpand,
  onRemove,
  onConfigChange,
}: CardSectionListItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.type })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  }

  const meta = CARD_SECTION_META[section.type]
  const Icon = meta.icon

  return (
    <div ref={setNodeRef} style={style}>
      {/* Row */}
      <div
        className={`
          group flex items-center gap-2 rounded-sm border px-3 py-2.5
          transition-colors
          ${isDragging ? 'scale-[1.02] border-sky-300 bg-sky-50 shadow-apple' : ''}
          ${isExpanded && !isDragging ? 'border-sky-500 bg-sky-50' : ''}
          ${!isExpanded && !isDragging ? 'border-gray-200 bg-white hover:border-gray-300' : ''}
        `}
      >
        {/* Drag handle */}
        <button
          className="flex-shrink-0 cursor-grab touch-none rounded-md p-0.5 text-gray-300 hover:text-gray-500 active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {/* Section icon */}
        <Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />

        {/* Section name */}
        <span className="flex-1 truncate text-sm font-medium text-gray-700">
          {meta.label}
        </span>

        {/* Expand chevron (only for sections with settings) */}
        {meta.hasSettings && (
          <button
            className="flex-shrink-0 p-0.5 text-gray-400 hover:text-gray-600"
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand()
            }}
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`}
            />
          </button>
        )}

        {/* Remove button */}
        <button
          className="flex-shrink-0 p-0.5 text-gray-400 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          aria-label={`Remove ${meta.label}`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Expandable settings */}
      {meta.hasSettings && isExpanded && (
        <div className="mt-1.5 overflow-hidden">
          <CardSectionSettings section={section} onConfigChange={onConfigChange} />
        </div>
      )}
    </div>
  )
}
